import { fmtDate, fmtMoney } from '../../lib/format';
import { portalQuotePdfUrl, type PortalData, type PortalBilling } from './api';

const QUOTE_STATUS: Record<PortalData['quotes'][number]['status'], { label: string; cls: string }> = {
  sent: { label: 'Čeká na schválení', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  accepted: { label: 'Přijata', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  rejected: { label: 'Odmítnuta', cls: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400' },
};

// Jeden řádek „faktura / platba" — co klient potřebuje vědět bez telefonu.
function BillingCell({ billing }: { billing: PortalBilling | null }) {
  if (!billing) {
    return <span className="text-zinc-400">Zatím nefakturováno</span>;
  }
  return (
    <div className="space-y-0.5">
      <div>
        <span className="font-medium">Vyfakturováno</span>{' '}
        <span className="whitespace-nowrap font-mono text-xs text-zinc-500">{billing.number}</span>
      </div>
      <div className="text-xs text-zinc-500">
        {billing.sent
          ? `odesláno${billing.sentAt ? ` ${fmtDate(billing.sentAt)}` : ''}`
          : 'vystaveno, zatím neodesláno'}
      </div>
    </div>
  );
}

function PaymentCell({ billing }: { billing: PortalBilling | null }) {
  if (!billing) return <span className="text-zinc-300 dark:text-zinc-600">—</span>;
  if (billing.paid) {
    return (
      <span className="font-medium text-emerald-700 dark:text-emerald-400">
        ✓ Zaplaceno{billing.paidAt ? <span className="block text-xs font-normal text-zinc-500">{fmtDate(billing.paidAt)}</span> : null}
      </span>
    );
  }
  return (
    <span className={billing.overdue ? 'font-medium text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}>
      {billing.overdue ? 'Po splatnosti' : 'Čeká na platbu'}
      {billing.dueAt && <span className="block text-xs font-normal text-zinc-500">splatnost {fmtDate(billing.dueAt)}</span>}
    </span>
  );
}

/**
 * Nabídky a fakturace na portálu zakázky. Klient tu vidí, co si objednal,
 * co mu přišlo k úhradě a co už je zaplacené — a nabídku si stáhne jako PDF.
 */
export default function PortalQuotes({ token, data }: { token: string; data: PortalData }) {
  if (data.quotes.length === 0) return null;
  const s = data.billingSummary;
  const remaining = s ? Math.round((s.invoiced - s.paid) * 100) / 100 : 0;

  return (
    <section className="mb-8">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500">Nabídky a fakturace</h2>

      {s && (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Objednáno" value={fmtMoney(s.ordered, s.currency)} />
          <Stat label="Vyfakturováno" value={fmtMoney(s.invoiced, s.currency)} />
          <Stat label="Zaplaceno" value={fmtMoney(s.paid, s.currency)} tone="ok" />
          <Stat label="Zbývá uhradit" value={fmtMoney(remaining, s.currency)} tone={remaining > 0 ? 'wait' : undefined} />
        </div>
      )}

      {/* Na telefonu karty — šest sloupců by se nevešlo a platba s PDF by
          skončila mimo obrazovku. */}
      <div className="space-y-2 sm:hidden">
        {data.quotes.map((q) => {
          const st = QUOTE_STATUS[q.status];
          const accepted = q.status === 'accepted';
          return (
            <div key={q.id} className={`rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900 ${q.status === 'rejected' ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium">{q.title || `Nabídka v${q.version}`}</div>
                  <div className="text-xs text-zinc-500">{q.department ? `${q.department} · ` : ''}vystavena {fmtDate(q.issuedAt)}</div>
                </div>
                <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
              </div>
              {accepted && (
                <div className="mt-2.5 grid grid-cols-2 gap-2 border-t border-zinc-100 pt-2.5 text-sm dark:border-zinc-800">
                  <BillingCell billing={q.billing} />
                  <div className="text-right"><PaymentCell billing={q.billing} /></div>
                </div>
              )}
              <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-zinc-100 pt-2.5 dark:border-zinc-800">
                <span className={`font-semibold tabular-nums ${q.status === 'rejected' ? 'line-through' : ''}`}>{fmtMoney(q.total, q.currency)}</span>
                <a
                  href={portalQuotePdfUrl(token, q.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md border border-zinc-300 px-3 py-1.5 text-xs font-medium dark:border-zinc-700"
                >
                  ⬇ PDF
                </a>
              </div>
            </div>
          );
        })}
      </div>

      <div className="hidden overflow-x-auto rounded-lg border border-zinc-200 bg-white sm:block dark:border-zinc-800 dark:bg-zinc-900">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wide text-zinc-500 dark:bg-zinc-900">
            <tr>
              <th className="px-4 py-2 font-medium">Nabídka</th>
              <th className="px-4 py-2 font-medium">Stav</th>
              <th className="px-4 py-2 font-medium">Faktura</th>
              <th className="px-4 py-2 font-medium">Platba</th>
              <th className="whitespace-nowrap px-4 py-2 text-right font-medium">Částka s DPH</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {data.quotes.map((q) => {
              const st = QUOTE_STATUS[q.status];
              const rejected = q.status === 'rejected';
              return (
                <tr key={q.id} className={rejected ? 'text-zinc-400' : undefined}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{q.title || `Nabídka v${q.version}`}</div>
                    <div className="text-xs text-zinc-500">{q.department ? `${q.department} · ` : ''}vystavena {fmtDate(q.issuedAt)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                  </td>
                  {/* U nepřijaté nabídky se fakturace neřeší — pomlčka místo „nefakturováno". */}
                  <td className="px-4 py-3">{q.status === 'accepted' ? <BillingCell billing={q.billing} /> : <span className="text-zinc-300 dark:text-zinc-600">—</span>}</td>
                  <td className="px-4 py-3">{q.status === 'accepted' ? <PaymentCell billing={q.billing} /> : <span className="text-zinc-300 dark:text-zinc-600">—</span>}</td>
                  <td className={`px-4 py-3 text-right tabular-nums ${rejected ? 'line-through' : ''}`}>{fmtMoney(q.total, q.currency)}</td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={portalQuotePdfUrl(token, q.id)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-zinc-300 px-2.5 py-1 text-xs font-medium hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-800"
                    >
                      ⬇ PDF
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'ok' | 'wait' }) {
  const cls = tone === 'ok'
    ? 'text-emerald-700 dark:text-emerald-400'
    : tone === 'wait'
      ? 'text-amber-700 dark:text-amber-400'
      : '';
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900">
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={`mt-0.5 text-base font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
